import { useEffect, useRef, useState } from 'react';
import { generateDxReportPdf } from '../dx-report-pdf';
import type { DxReportPayload } from '../types';

export type DxReportPdfState =
    | { kind: 'building' }
    | { kind: 'ready'; url: string; blob: Blob }
    | { kind: 'error'; message: string };

// Builds once on mount; callers remount to rebuild with a new payload.
export function useDxReportPdf(payload: DxReportPayload): DxReportPdfState {
    const [state, setState] = useState<DxReportPdfState>({ kind: 'building' });
    const activeUrlRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setState({ kind: 'building' });

        generateDxReportPdf(payload)
            .then((blob) => {
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
                activeUrlRef.current = url;
                setState({ kind: 'ready', url, blob });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : 'Unknown error';
                setState({ kind: 'error', message: `Failed to build PDF: ${message}` });
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            if (activeUrlRef.current) {
                URL.revokeObjectURL(activeUrlRef.current);
                activeUrlRef.current = null;
            }
        };
    }, []);

    return state;
}
